/**
 * Cross-tab coordination so that a native-notification click focuses the
 * ALREADY-OPEN app tab on the same backend port (bport) instead of opening a
 * new tab.
 *
 * Design:
 * - Every tab opens a BroadcastChannel and keeps a unique tabId.
 * - Each tab knows its own current bport (read from the URL search param).
 * - A notification-opened tab (marked with focus=1) calls requestFocus(bport).
 * - The tab whose current bport matches the requested bport replies, brings
 *   itself to the front (window.focus), and reconciles streaming state so the
 *   finished result shows.
 * - The notification-opened tab then closes itself (best-effort).
 *
 * Safety: every failure path (unsupported BroadcastChannel, no matching tab,
 * invalid params, thrown errors) degrades to "render the app normally".
 */

import { useSessionStore } from '@/store/sessionStore';

const CHANNEL_NAME = 'repoburg-tabs';
const FOCUS_TIMEOUT_MS = 400;

type TabMessage =
  | { type: 'focus-request'; bport: number; from: string }
  | { type: 'focus-ack'; to: string; from: string }
  | { type: 'tab-online'; tabId: string; bport: number | null }
  | { type: 'tab-offline'; tabId: string };

let channel: BroadcastChannel | null = null;
let tabId: string | null = null;

/** Read this tab's current backend port from the URL `bport` search param. */
function currentBport(): number | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('bport');
    if (raw) {
      const port = parseInt(raw, 10);
      return isNaN(port) ? null : port;
    }
  } catch {
    // URL parsing can fail in some environments; treat as no bport.
  }
  return null;
}

/** Generate a unique id for this tab. */
function makeTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to a timestamp-based id.
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Set up the BroadcastChannel listener. Safe to call once per tab; idempotent.
 * If BroadcastChannel is unsupported, this is a no-op (no coordination, but
 * the app still works normally).
 */
export function initTabCoordinator(): void {
  if (channel) return; // already initialized

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    // BroadcastChannel not supported: coordination disabled, app still works.
    channel = null;
    return;
  }

  tabId = makeTabId();

  // Announce presence so other tabs know we exist (informational).
  try {
    channel.postMessage({ type: 'tab-online', tabId, bport: currentBport() });
  } catch {
    // Non-critical.
  }

  channel.onmessage = (ev: MessageEvent<TabMessage>) => {
    const msg = ev.data;
    if (!msg || !channel || !tabId) return;

    switch (msg.type) {
      case 'focus-request': {
        // Only the tab whose current bport matches the request responds.
        const myBport = currentBport();
        if (myBport !== null && myBport === msg.bport) {
          try {
            window.focus();
          } catch {
            // window.focus can throw in restricted contexts; ignore.
          }
          try {
            // The finished result may have been missed while this tab was
            // backgrounded; reconcile so the completed state shows.
            useSessionStore.getState().reconcileStreamingState();
          } catch {
            // Reconcile is best-effort.
          }
          try {
            channel.postMessage({ type: 'focus-ack', to: msg.from, from: tabId });
          } catch {
            // Ack delivery is best-effort.
          }
        }
        break;
      }
      default:
        break;
    }
  };

  // Announce departure so other tabs can update their live-set (informational).
  window.addEventListener('beforeunload', () => {
    if (channel && tabId) {
      try {
        channel.postMessage({ type: 'tab-offline', tabId });
      } catch {
        // Non-critical.
      }
    }
  });
}

/**
 * Ask an already-open tab on the given bport to focus itself.
 * Resolves true if a matching tab acknowledged within the timeout.
 * Resolves false if no matching tab answered (caller should stay open).
 * Never rejects.
 */
export function requestFocus(bport: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (!channel || !tabId) {
      // Coordinator not initialized or unsupported: no match possible.
      resolve(false);
      return;
    }

    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      channel!.removeEventListener('message', handler);
      resolve(ok);
    };

    const handler = (ev: MessageEvent<TabMessage>) => {
      const msg = ev.data;
      if (msg && msg.type === 'focus-ack' && msg.to === tabId) {
        finish(true);
      }
    };

    channel.addEventListener('message', handler);

    const timer = setTimeout(() => finish(false), FOCUS_TIMEOUT_MS);

    try {
      channel.postMessage({ type: 'focus-request', bport, from: tabId });
    } catch {
      finish(false);
    }
  });
}